import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { useOppositions } from '@/features/director-professors/hooks/useSubjects';
import { useShifts } from '@/features/director-schedules/hooks/useShifts';
import { useConvertProspect } from '../hooks/useProspects';
/** Hoy en ISO local (para el default del input date). */
function todayISO() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
export function ConvertProspectForm({ prospect, onSuccess }) {
    const [serverError, setServerError] = useState('');
    const [created, setCreated] = useState(null);
    const convertProspect = useConvertProspect(prospect.id);
    const { data: oppositions = [], isLoading: oppLoading } = useOppositions();
    const enabledOppositions = oppositions.filter((o) => o.is_enabled);
    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            first_name: prospect.first_name,
            last_name: prospect.last_name,
            email: prospect.email,
            phone: prospect.phone ?? '',
            dni: '',
            opposition_id: '',
            shift_id: '',
            address: '',
            birth_date: '',
            start_date: todayISO(),
        },
    });
    const oppositionId = watch('opposition_id');
    const { data: shifts = [], isLoading: shiftsLoading } = useShifts(oppositionId || null);
    useEffect(() => {
        const first = enabledOppositions[0];
        if (!oppositionId && first)
            setValue('opposition_id', first.id);
    }, [enabledOppositions, oppositionId, setValue]);
    useEffect(() => {
        setValue('shift_id', '');
    }, [oppositionId, setValue]);
    function onSubmit(values) {
        setServerError('');
        if (!values.opposition_id || !values.shift_id) {
            setServerError('Selecciona oposición y turno.');
            return;
        }
        convertProspect.mutate({
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            email: values.email.trim(),
            phone: values.phone.trim() || undefined,
            dni: values.dni.trim().toUpperCase(),
            opposition_id: values.opposition_id,
            shift_id: values.shift_id,
            address: values.address.trim() || undefined,
            birth_date: values.birth_date || undefined,
            start_date: values.start_date || undefined,
        }, {
            onSuccess: (student) => {
                if (student.temp_password) {
                    setCreated({ email: values.email.trim(), password: student.temp_password });
                }
                else {
                    onSuccess();
                }
            },
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    const shiftPlaceholder = !oppositionId
        ? 'Selecciona oposición primero'
        : shiftsLoading
            ? 'Cargando…'
            : shifts.length === 0
                ? 'No hay turnos en esta oposición'
                : 'Seleccionar turno';
    if (created) {
        return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "rounded-xl border border-green-200 bg-green-50 p-4 text-sm dark:border-green-900 dark:bg-green-950/40", children: [_jsx("p", { className: "font-semibold text-green-800 dark:text-green-300", children: "Alumno matriculado correctamente" }), _jsx("p", { className: "mt-1 text-gray-600 dark:text-gray-400", children: "Entrega estas credenciales al alumno. La contrase\u00F1a es temporal: deber\u00E1 cambiarla al iniciar sesi\u00F3n. No volver\u00E1 a mostrarse." }), _jsxs("dl", { className: "mt-3 space-y-1 font-mono text-sm break-all text-gray-900 dark:text-gray-100", children: [_jsxs("div", { children: [_jsx("span", { className: "text-gray-500 dark:text-gray-400", children: "Email: " }), created.email] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-500 dark:text-gray-400", children: "Contrase\u00F1a: " }), created.password] })] })] }), _jsx(Button, { type: "button", onClick: onSuccess, className: "w-full", children: "Hecho" })] }));
    }
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [_jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Input, { label: "Nombre", error: errors.first_name?.message, ...register('first_name', { required: 'El nombre es obligatorio', minLength: { value: 2, message: 'Mínimo 2 caracteres' } }) }), _jsx(Input, { label: "Apellidos", error: errors.last_name?.message, ...register('last_name', { required: 'Los apellidos son obligatorios', minLength: { value: 2, message: 'Mínimo 2 caracteres' } }) })] }), _jsx(Input, { label: "Email", type: "email", error: errors.email?.message, ...register('email', {
                    required: 'El email es obligatorio',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                }) }), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Input, { label: "DNI", error: errors.dni?.message, hint: "Ejemplo: 12345678A", ...register('dni', {
                            required: 'El DNI es obligatorio',
                            pattern: { value: /^\d{8}[A-Za-z]$/, message: 'Formato: 12345678A' },
                        }) }), _jsx(Input, { label: "Tel\u00E9fono (opcional)", error: errors.phone?.message, ...register('phone', {
                            pattern: { value: /^[6789]\d{8}$/, message: 'Teléfono inválido' },
                        }) })] }), _jsx(Select, { label: "Oposici\u00F3n", disabled: oppLoading, options: [
                    { value: '', label: oppLoading ? 'Cargando…' : 'Seleccionar oposición' },
                    ...enabledOppositions.map((o) => ({ value: o.id, label: o.name })),
                ], error: errors.opposition_id?.message, ...register('opposition_id', { required: 'La oposición es obligatoria' }) }), _jsx(Select, { label: "Turno", disabled: !oppositionId || shiftsLoading, options: [
                    { value: '', label: shiftPlaceholder },
                    ...shifts.map((s) => ({ value: s.id, label: s.name })),
                ], error: errors.shift_id?.message, ...register('shift_id', { required: 'El turno es obligatorio' }) }), _jsx(Input, { label: "Direcci\u00F3n (opcional)", ...register('address') }), _jsx(Input, { label: "Fecha de nacimiento (opcional)", type: "date", ...register('birth_date') }), _jsxs("div", { children: [_jsx(Input, { label: "Fecha de inicio", type: "date", ...register('start_date') }), _jsx("p", { className: "mt-1 text-xs text-gray-500 dark:text-gray-400", children: "Por defecto hoy. Si empieza m\u00E1s adelante, no aparecer\u00E1 al pasar lista ni se le generar\u00E1n mensualidades hasta que llegue ese mes." })] }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: convertProspect.isPending, className: "w-full", children: "Convertir en alumno" })] }));
}
//# sourceMappingURL=ConvertProspectForm.js.map